/**
 * OpsSsoPage
 * ==========
 *
 * SSO authorization page for the OPS CONSOLE (``/sso/ops``). The ops
 * panel's login button lands here with:
 *
 *   ?redirect_uri=<ops callback URL>&state=<ops CSRF state>
 *
 * With the operator's existing session (TOTP included) this page asks
 * the API for a ONE-TIME sign-in code (``POST /user/ops/authorize`` —
 * single-use, ~60s TTL, bound to the exact redirect_uri, which the
 * server validates against its own allowlist) and bounces the browser
 * to ``<redirect_uri>?code=...&state=...``. The ops panel exchanges
 * the code server-to-server, runs its own admission + 2FA checks, and
 * mints its own session. No JWT ever crosses the boundary.
 *
 * Works logged-out too: the query params are stashed in localStorage
 * (``pendingOpsSso``) and the user is sent to the login page — the
 * post-login bootstrap (login.html / verify-magic-link.html) brings
 * them straight back here to finish.
 *
 * The final hop requires an explicit click so a stray link can never
 * silently open an ops session.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Link, Paper, Stack, Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

import config from '../../config';
import { fetchRequest as logout } from '../../redux/actions/auth/logout';
import { getLocalUser, isAuthenticated, getCurrentUser } from '../../utils/localUser';

const PENDING_KEY = 'pendingOpsSso';

const OpsSsoPage = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  // Params come from the URL, or — right after a login round-trip —
  // from the stash written before we handed off to the login page.
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fromUrl = {
      redirect_uri: searchParams.get('redirect_uri') || '',
      state: searchParams.get('state') || '',
    };
    if (fromUrl.redirect_uri) {
      setTarget(fromUrl);
      return;
    }
    try {
      const stashed = localStorage.getItem(PENDING_KEY);
      if (stashed) {
        localStorage.removeItem(PENDING_KEY);
        const parsed = JSON.parse(stashed);
        if (parsed && parsed.redirect_uri) {
          setTarget(parsed);
          return;
        }
      }
    } catch (e) { /* fall through to the error state */ }
    setTarget({ redirect_uri: '', state: '' });
  }, [searchParams]);

  const authed = isAuthenticated();
  const me = authed ? getCurrentUser() : null;

  const handleSignIn = () => {
    localStorage.setItem(PENDING_KEY, JSON.stringify(target));
    window.location.href = '/login.html';
  };

  // "Not you?" — stash the pending handoff (the logout saga leaves it
  // untouched), sign the current account out, and let the post-login
  // bootstrap bring the next account straight back here.
  const handleSwitchAccount = () => {
    localStorage.setItem(PENDING_KEY, JSON.stringify(target));
    dispatch(logout());
  };

  const authorize = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${config.API_URL}/user/ops/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getLocalUser(),
        },
        body: JSON.stringify({ redirect_uri: target.redirect_uri }),
      });
      const body = await response.json().catch(() => ({}));
      const data = body.data || body;
      if (!response.ok || !data.code) {
        const message = data.message || body.status
          || 'Could not start the ops-console sign-in. Please try again.';
        setError(message);
        setBusy(false);
        return;
      }
      const url = new URL(target.redirect_uri);
      url.searchParams.set('code', data.code);
      if (target.state) url.searchParams.set('state', target.state);
      window.location.href = url.toString();
    } catch (e) {
      setError('Could not reach the API. Please try again.');
      setBusy(false);
    }
  }, [target]);

  let destinationHost = '';
  try {
    destinationHost = target && target.redirect_uri
      ? new URL(target.redirect_uri).host : '';
  } catch (e) { /* invalid URI — the server would reject it anyway */ }

  return (
    <Box
      sx={{
        // The app shell (.App) is a flex row; without an explicit width
        // this page would shrink to its content and sit off-center.
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 460, width: '100%', borderRadius: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <AdminPanelSettingsIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Ops Console sign-in
          </Typography>
        </Stack>

        {target === null && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        )}

        {target !== null && !target.redirect_uri && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              This page can only be opened from the Ops Console&apos;s login
              button.
            </Alert>
            <Button variant="outlined" onClick={() => { window.location.href = '/'; }}>
              Go to the app
            </Button>
          </>
        )}

        {target !== null && target.redirect_uri && !authed && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Sign in to your Predict.ai account first — you&apos;ll be
              brought right back here.
            </Alert>
            <Button variant="contained" fullWidth onClick={handleSignIn}>
              Sign in to continue
            </Button>
          </>
        )}

        {target !== null && target.redirect_uri && authed && (
          <>
            <Typography variant="body1" sx={{ mb: 2.5 }}>
              Continue to the Ops Console at <strong>{destinationHost}</strong>
              {me && me.email ? (
                <> as <strong>{me.email}</strong></>
              ) : null}
              ?
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Button
              variant="contained"
              fullWidth
              onClick={authorize}
              disabled={busy}
            >
              {busy ? 'Redirecting…' : 'Continue to Ops Console'}
            </Button>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, textAlign: 'center' }}
            >
              Not you?{' '}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleSwitchAccount}
                disabled={busy}
                sx={{ verticalAlign: 'baseline' }}
              >
                Sign out and use another account
              </Link>
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default OpsSsoPage;
